import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { interval, map, startWith } from 'rxjs';
import { UserService } from '../../user/user.service';
import { LanguageService } from '../../language.service';
import {
   DEFAULT_STORE_TIMEZONE,
   deviceTimeZoneMismatch,
   formatUtcOffset,
} from '../../shared/timezone.util';

/**
 * Warns when this device's clock runs in a different time zone than the
 * store. Most screens build "today" and "this month" from the device clock
 * (attendance, payroll runs, reports, bill list), while Cloud Functions use
 * the store's zone — so a phone left on UTC shows and calculates dates a day
 * off. Lives at the app root so it also covers Sell / KOT screens.
 */
@Component({
   selector: 'app-timezone-mismatch-banner',
   changeDetection: ChangeDetectionStrategy.OnPush,
   imports: [MatIconModule],
   template: `
      @if (message(); as msg) {
         <div
            role="alert"
            class="fixed bottom-4 left-1/2 z-[10000] flex w-[min(640px,calc(100vw-32px))] -translate-x-1/2 items-start gap-3 rounded-xl border border-[var(--kt-warning)] bg-[var(--kt-surface-card)] [background-image:linear-gradient(var(--kt-warning-bg),var(--kt-warning-bg))] px-4 py-3 text-sm font-medium text-[var(--kt-warning-darker)] shadow-lg"
         >
            <mat-icon class="shrink-0" aria-hidden="true">schedule</mat-icon>
            <span class="flex-1 leading-snug">{{ msg }}</span>
            <button
               type="button"
               class="shrink-0 inline-flex items-center justify-center rounded-md p-1 opacity-80 hover:opacity-100 focus-visible:outline focus-visible:outline-2"
               [attr.aria-label]="lang() === 'en' ? 'Dismiss' : 'បិទ'"
               (click)="dismissed.set(true)"
            >
               <mat-icon aria-hidden="true">close</mat-icon>
            </button>
         </div>
      }
   `,
})
export class TimezoneMismatchBannerComponent {
   private readonly userService = inject(UserService);
   readonly lang = toSignal(inject(LanguageService).getLanguage, { initialValue: 'en' });
   private readonly store = toSignal(this.userService.currentStore$, { initialValue: null });

   /** Re-checked every 10 minutes, so fixing the device setting clears it. */
   private readonly now = toSignal(
      interval(10 * 60 * 1000).pipe(
         startWith(0),
         map(() => new Date()),
      ),
      { initialValue: new Date() },
   );

   readonly dismissed = signal(false);

   readonly message = computed(() => {
      const store = this.store();
      if (!store || this.dismissed()) return null;
      const storeZone = store.timezone || DEFAULT_STORE_TIMEZONE;
      const mismatch = deviceTimeZoneMismatch(this.now(), storeZone);
      if (!mismatch) return null;

      const device = formatUtcOffset(mismatch.deviceOffset);
      const storeOffset = formatUtcOffset(mismatch.storeOffset);
      return this.lang() === 'en'
         ? `This device's time zone is ${device}, but the store uses ${storeZone} (${storeOffset}). ` +
              'Dates in attendance, payroll and reports can be off by a day. ' +
              `Set the device's time zone to ${storeZone}.`
         : `ម៉ោងឧបករណ៍នេះគឺ ${device} ប៉ុន្តែហាងប្រើ ${storeZone} (${storeOffset})។ ` +
              'កាលបរិច្ឆេទក្នុងវត្តមាន បញ្ជីប្រាក់ខែ និងរបាយការណ៍ អាចខុសមួយថ្ងៃ។ ' +
              `សូមកំណត់តំបន់ម៉ោងឧបករណ៍ទៅ ${storeZone}។`;
   });
}
